import {
  type MessagePathRecord,
  type MessagePathRepresentationMapping,
} from "./MessagePathRecord";
import { type RepresentationRecord } from "./RepresentationRecord";
import { type TopicRecord } from "./TopicRecord";

/**
 * Compute a topic's message-path → representation mappings from an
 * already-hydrated {@link TopicRecord}, without a network round trip.
 *
 * This is a client-side mirror of the server's
 * `Topic.get_message_path_representations`
 * (`packages/roboto_service/src/roboto_service/domain/topics/topic.py`) —
 * the logic behind `GET v1/topics/id/{topic_id}/message-path/representations`.
 * It exists so callers that already hold the full `TopicRecord` (e.g. the
 * visualization workspace, which hydrates every topic with its message paths
 * and representations) can skip that request. Keep the two implementations in
 * sync; behavioral divergence selects a different-but-valid representation
 * rather than failing, so drift is easy to miss.
 *
 * Semantics mirrored from the server:
 * - Only "leaf" message paths are mapped; a path that is a prefix of another
 *   path is excluded.
 * - A leaf's own representations win; per `(format, transformations)` variant
 *   only the newest (by `created`) survives.
 * - A leaf without its own representations inherits from its nearest ancestor
 *   path that has any.
 * - Leaves still uncovered fall back to the topic's default representation,
 *   when one exists.
 *
 * The server's optional `storage_format` filter is deliberately not mirrored:
 * no client call site uses it.
 */
export function messagePathRepresentationMappings(
  topic: TopicRecord,
): MessagePathRepresentationMapping[] {
  const defaultRepresentation = topic.default_representation ?? undefined;

  const messagePathById = new Map<string, MessagePathRecord>();
  const representationById = new Map<string, RepresentationRecord>();
  // Insertion-ordered, like the server's defaultdict; values are message_path_ids.
  const messagePathIdsByRepresentationId = new Map<string, Set<string>>();

  const addMapping = (
    representation: RepresentationRecord,
    messagePathId: string,
  ) => {
    representationById.set(representation.representation_id, representation);
    let ids = messagePathIdsByRepresentationId.get(
      representation.representation_id,
    );
    if (ids === undefined) {
      ids = new Set();
      messagePathIdsByRepresentationId.set(
        representation.representation_id,
        ids,
      );
    }
    ids.add(messagePathId);
  };

  for (const record of leafMessagePaths(topic.message_paths)) {
    messagePathById.set(record.message_path_id, record);

    // Does this message path have its own representations?
    const ownRepresentations = canonicalRepresentationsForMessagePath(record);
    if (ownRepresentations.length > 0) {
      for (const representation of ownRepresentations) {
        addMapping(representation, record.message_path_id);
      }
      continue;
    }

    // Else, does its nearest ancestor with representations cover it?
    for (const parentPath of parentPaths(record.path_in_schema)) {
      const parentRecord = topic.message_paths.find(
        (mp) => mp.message_path === parentPath,
      );
      if (parentRecord === undefined) {
        continue;
      }
      const parentRepresentations =
        canonicalRepresentationsForMessagePath(parentRecord);
      if (parentRepresentations.length > 0) {
        for (const representation of parentRepresentations) {
          addMapping(representation, record.message_path_id);
        }
        break;
      }
    }

    // Still uncovered: fall back to the topic's default representation.
    const covered = [...messagePathIdsByRepresentationId.values()].some((ids) =>
      ids.has(record.message_path_id),
    );
    if (!covered && defaultRepresentation !== undefined) {
      addMapping(defaultRepresentation, record.message_path_id);
    }
  }

  return [...messagePathIdsByRepresentationId.entries()].map(
    ([representationId, messagePathIds]) => {
      const representation = representationById.get(representationId);
      if (representation === undefined) {
        // Unreachable: addMapping always registers the representation.
        throw new Error(`Representation ${representationId} not registered`);
      }
      return {
        representation,
        message_paths: [...messagePathIds].map((id) => {
          const messagePath = messagePathById.get(id);
          if (messagePath === undefined) {
            // Unreachable: only registered leaf ids are added to the map.
            throw new Error(`Message path ${id} not registered`);
          }
          return messagePath;
        }),
      };
    },
  );
}

/**
 * Leaf message paths only: records whose `message_path` is not a
 * `path_in_schema` prefix of any other record.
 */
function leafMessagePaths(
  records: readonly MessagePathRecord[],
): MessagePathRecord[] {
  if (records.length === 0) {
    return [];
  }

  // Last record wins on duplicate paths, matching the server's dict build.
  const recordByPath = new Map(
    records.map((record) => [record.message_path, record]),
  );

  const parentPathSet = new Set<string>();
  for (const record of recordByPath.values()) {
    const parts = record.path_in_schema;
    for (let i = 1; i < parts.length; i++) {
      parentPathSet.add(parts.slice(0, i).join("."));
    }
  }

  return [...recordByPath.entries()]
    .filter(([path]) => !parentPathSet.has(path))
    .map(([, record]) => record);
}

/**
 * Ancestor paths of `pathInSchema`, most specific first — mirrors the SDK's
 * `MessagePath.parents` (dot-joined `path_in_schema` prefixes).
 */
function parentPaths(pathInSchema: readonly string[]): string[] {
  const parents: string[] = [];
  for (let length = pathInSchema.length - 1; length >= 1; length--) {
    parents.push(pathInSchema.slice(0, length).join("."));
  }
  return parents;
}

/**
 * One representation per `(format, transformations)` variant, newest `created`
 * winning — mirrors the server's `__matching_representations_for_message_path`
 * (with no storage-format filter).
 */
function canonicalRepresentationsForMessagePath(
  record: MessagePathRecord,
): RepresentationRecord[] {
  const newestFirst = [...record.representations].sort(
    (a, b) => createdEpochMs(b) - createdEpochMs(a),
  );

  const seenVariants = new Set<string>();
  const canonical: RepresentationRecord[] = [];
  for (const representation of newestFirst) {
    const variantKey = JSON.stringify([
      representation.format ?? null,
      representation.transformations ?? [],
    ]);
    if (seenVariants.has(variantKey)) {
      continue;
    }
    seenVariants.add(variantKey);
    canonical.push(representation);
  }
  return canonical;
}

function createdEpochMs(representation: RepresentationRecord): number {
  if (representation.created === undefined) {
    return Number.NEGATIVE_INFINITY;
  }
  const parsed = Date.parse(representation.created);
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}
